package com.heelion.farmafreelance.agent

import com.heelion.farmafreelance.models.Professione
import com.heelion.farmafreelance.models.RicercaSalvata
import com.heelion.farmafreelance.models.UserRole
import com.heelion.farmafreelance.models.isProfessionista
import com.heelion.farmafreelance.voice.VoiceCommandParser
import kotlinx.datetime.isoDayNumber

/**
 * "Avvisami quando c'è un turno a Roma il sabato da almeno 30 euro": dalla frase a una
 * [RicercaSalvata]. Il server avvisa (campanella + push) quando qualcuno pubblica un turno
 * compatibile. Diverso dai promemoria di Farmy ("avvisami dei turni da confermare"), che
 * riguardano i propri turni già esistenti.
 */
object AvvisiFarmy {
    private val RICHIESTA = Regex(
        "^(?:ok |per favore )?(avvisami|avvertimi|notificami|fammi sapere|segnalami|dimmi|voglio sapere)\\b.*\\b(quando|se|appena|non appena)\\b"
    )
    // Queste frasi sono promemoria sui propri turni, non ricerche di nuovi turni.
    private val PROMEMORIA = Regex("confermare|in attesa|disdic|disdett|annull|giornata|imminent|sera prima|ore prima")
    private val OGGETTO = Regex("turn|lavoro|farmaci|infermier|disponibil|offert|annunc")

    fun eRichiestaAvviso(testo: String): Boolean {
        val t = IntentClassifier.normalizza(testo)
        return RICHIESTA.containsMatchIn(t) && OGGETTO.containsMatchIn(t) && !PROMEMORIA.containsMatchIn(t)
    }

    /**
     * La ricerca da salvare per [utenteId] ("FARMACISTA_12") con ruolo [ruolo]; null per chi non può salvarne.
     * [zona] = provincia del profilo, usata per "in zona" / "vicino a me".
     */
    fun ricerca(testo: String, utenteId: String, ruolo: UserRole, zona: String? = null): RicercaSalvata? {
        if (ruolo == UserRole.ADMIN) return null
        // "in zona" non è un comune: il parser lo leggerebbe come "Zona".
        val luogo = RicercaFarmacisti.senzaMiaZona(testo)
        val comuneDetto = VoiceCommandParser.comuneMenzionato(luogo)
        val provincia = VoiceCommandParser.provinciaMenzionata(luogo)?.let { Province.sigla(it) ?: it }
            ?: comuneDetto?.let { Province.sigla(it) }
            ?: zona?.takeIf { comuneDetto == null && it.isNotBlank() && RicercaFarmacisti.nellaMiaZona(testo) }
        // Un comune che non è capoluogo resta un filtro sul comune.
        val comune = comuneDetto?.takeIf { Province.sigla(it) == null }
        val giorni = VoiceCommandParser.giorniMenzionati(testo).map { it.isoDayNumber }.sorted()
        val compenso = VoiceCommandParser.compensoMenzionato(testo)
        // Farmacista o infermiere: cerca turni delle farmacie (con un compenso minimo).
        val farmacista = ruolo.isProfessionista
        // La farmacia cerca farmacisti, o infermieri se la frase li nomina.
        val cercata = if (farmacista) null else RicercaFarmacisti.professioneMenzionata(testo)
        val competenza = cercata?.let { RicercaFarmacisti.competenzaMenzionata(testo, it) }
        return RicercaSalvata(
            utenteId = utenteId,
            ruolo = ruolo.name,
            provincia = provincia,
            comune = comune,
            giorniSettimana = giorni,
            compensoMin = compenso.takeIf { farmacista },
            compensoMax = compenso.takeIf { !farmacista },
            competenza = competenza,
            professione = cercata,
            descrizione = descrizione(farmacista, provincia, comune, giorni, compenso, competenza, cercata)
        )
    }

    private val GIORNI = listOf("lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato", "domenica")

    /** "turni di farmacie in RM, il sabato, da 30 €/h" / "farmacisti con galenica in RM, fino a 35 €/h" / "infermieri disponibili…" */
    fun descrizione(
        farmacista: Boolean, provincia: String?, comune: String?, giorni: List<Int>, compenso: Double?, competenza: String?,
        cercata: Professione? = null
    ): String =
        listOfNotNull(
            when {
                farmacista -> "turni di farmacie"
                cercata == Professione.INFERMIERE -> "infermieri disponibili"
                else -> "farmacisti disponibili"
            },
            competenza?.let { "con ${it.lowercase()}" },
            comune?.let { "a $it" } ?: provincia?.let { "in $it" },
            giorni.takeIf { it.isNotEmpty() }?.joinToString(" e ") { GIORNI[it - 1] }?.let { (if (it.startsWith("domenica")) "la " else "il ") + it },
            compenso?.let { (if (farmacista) "da " else "fino a ") + (if (it % 1.0 == 0.0) it.toInt().toString() else it.toString().replace('.', ',')) + " €/h" }
        ).joinToString(", ").let { if (provincia == null && comune == null) "$it, in tutta Italia" else it }

}
